import type { Feature, FeatureCollection } from 'geojson'
import { normalizePoseKeyframes, poseLocationRuns } from './format'
import { findWind, isLocalStorm, isPlumeSource } from './resolveSession'
import { BRWN_PLUME_ZONE_KIND, isBrwnPlumeType } from './eggs/brwnPlume'
import { paletteLabel } from './palette'
import { zoneCfgFor } from './toolUi'
import type { ResolvedObject } from './types'

/** Map plume fill/stroke — purple = tornado, blue = hurricane, cyan = Surface wind. */
export const PLUME_PAINT = {
  tornado: { fill: '#A56BFF', line: '#C9A6FF' },
  hurricane: { fill: '#4C8FE0', line: '#A9C8F0' },
  surface: { fill: '#2EB8C9', line: '#7AD4E0' },
} as const

export type PlumeWindSource = keyof typeof PLUME_PAINT

/** Minimal tap shape for zoom scoring (avoids importing ScenarioMapHost). */
export type MapFeatureScoreTap = {
  kind: 'pipeline' | 'road'
  properties: Record<string, unknown>
  path: [number, number][]
  segments: [number, number][][]
}

/**
 * Future filter hook: at wide zoom prefer long / high-class roads; at tight zoom
 * local streets dominate. Not applied yet — Find nearby still returns every hit
 * in the search radius so we can judge tile quality. When we filter, combine this
 * score with distance (e.g. keep top N by score, or drop below a zoom-scaled floor).
 */
export function mapFeatureZoomScore(tap: MapFeatureScoreTap, zoom: number): number {
  if (tap.kind === 'pipeline') {
    // Pipes are usually scarce — keep them visible at every zoom.
    return 80
  }
  const cls = String(tap.properties.class ?? tap.properties.type ?? '').toLowerCase()
  const lenApprox = Math.max(
    tap.path.length,
    tap.segments.reduce((n, s) => n + s.length, 0),
  )
  let classScore = 20
  if (/motorway|trunk|motorway_link|trunk_link/.test(cls)) classScore = 100
  else if (/primary|primary_link/.test(cls)) classScore = 85
  else if (/secondary|secondary_link/.test(cls)) classScore = 65
  else if (/tertiary|tertiary_link/.test(cls)) classScore = 45
  else if (/street|residential|unclassified|service|living_street/.test(cls)) classScore = 25
  else if (/path|footway|cycleway|track|pedestrian/.test(cls)) classScore = 10
  // Wide zoom: class dominates. Tight zoom: local length / density matters more.
  const classWeight = zoom < 14 ? 0.85 : zoom < 16 ? 0.55 : 0.3
  const lengthWeight = 1 - classWeight
  const lengthScore = Math.min(100, lenApprox)
  return classScore * classWeight + lengthScore * lengthWeight
}

function ringAround(lng: number, lat: number, radiusM: number, steps = 64): [number, number][] {
  const ring: [number, number][] = []
  const dLat = radiusM / 111_320
  const dLng = radiusM / (111_320 * Math.cos((lat * Math.PI) / 180))
  for (let i = 0; i <= steps; i++) {
    const a = (2 * Math.PI * i) / steps
    ring.push([lng + dLng * Math.sin(a), lat + dLat * Math.cos(a)])
  }
  return ring
}

/** Downwind arrow from halfway out to the ring edge, so the shaft clears the icon. */
function directionArrow(
  lng: number,
  lat: number,
  fromDeg: number,
  radiusM: number,
): [number, number][] {
  const down = (((fromDeg + 180) % 360) * Math.PI) / 180
  const mLat = 1 / 111_320
  const mLng = 1 / (111_320 * Math.cos((lat * Math.PI) / 180))
  const at = (along: number, side: number): [number, number] => {
    const north = along * Math.cos(down) + side * Math.cos(down - Math.PI / 2)
    const east = along * Math.sin(down) + side * Math.sin(down - Math.PI / 2)
    return [lng + east * mLng, lat + north * mLat]
  }
  // Tail starts at half the radius so the shaft clears the icon.
  const tail = radiusM * 0.5
  const neck = radiusM * 0.78
  const head = radiusM * 0.16
  const shaft = radiusM * 0.045
  return [
    at(radiusM, 0),
    at(neck, head),
    at(neck, shaft),
    at(tail, shaft),
    at(tail, -shaft),
    at(neck, -shaft),
    at(neck, -head),
    at(radiusM, 0),
  ]
}

function plumeWedge(
  lng: number,
  lat: number,
  fromDeg: number,
  distM: number,
  spreadDeg: number,
): [number, number][] {
  const downRad = ((fromDeg + 180) % 360) * (Math.PI / 180)
  const half = (spreadDeg / 2) * (Math.PI / 180)
  const dLat = distM / 111_320
  const dLng = distM / (111_320 * Math.cos((lat * Math.PI) / 180))
  const coords: [number, number][] = [[lng, lat]]
  const steps = 24
  for (let i = 0; i <= steps; i++) {
    const b = downRad - half + (2 * half * i) / steps
    coords.push([lng + dLng * Math.sin(b), lat + dLat * Math.cos(b)])
  }
  coords.push([lng, lat])
  return coords
}

type WindSample = {
  fromDeg: number
  plumeDistanceM: number
  plumeSpreadDeg: number
  plumesVisible?: boolean
  /** Which force shaped this sample — drives plume color on the map. */
  source: PlumeWindSource
}

function windSampleFromParams(
  params: Record<string, unknown>,
  source: PlumeWindSource,
): WindSample | null {
  const fromDeg = Number(params.fromDeg) || 0
  const distM = Number(params.plumeDistanceM) || 0
  const spread = Number(params.plumeSpreadDeg) || 20
  if (distM <= 0) return null
  return {
    fromDeg,
    plumeDistanceM: distM,
    plumeSpreadDeg: spread,
    plumesVisible: params.plumesVisible === false ? false : undefined,
    source,
  }
}

/** Visible impact ring. `radiusM` wins; older boards stored the cutoff as plume distance. */
export function tornadoImpactRadiusM(obj: {
  params?: Record<string, unknown>
  windKeyframes?: readonly { plumeDistanceM?: number }[]
}): number {
  const radius = Number(obj.params?.radiusM)
  if (Number.isFinite(radius) && radius > 0) return radius
  const frames = obj.windKeyframes ?? []
  if (frames.length) {
    const peak = Math.max(...frames.map((frame) => Number(frame.plumeDistanceM) || 0))
    if (peak > 0) return peak
  }
  const legacy = Number(obj.params?.plumeDistanceM)
  if (Number.isFinite(legacy) && legacy > 0) return legacy
  return 800
}

function stormWindSource(type: string): PlumeWindSource {
  return type === 'hurricane' ? 'hurricane' : 'tornado'
}

/**
 * Local storm wind when a point sits inside a visible tornado or hurricane ring;
 * otherwise board Surface wind. The nearest center wins. The ring test matches
 * the drawn circle.
 */
export function effectiveWindAt(
  objects: readonly ResolvedObject[],
  at: { lng: number; lat: number } | null,
  forObject?: ResolvedObject,
): WindSample | null {
  if (forObject && isLocalStorm(forObject.type)) {
    return windSampleFromParams(forObject.params, stormWindSource(forObject.type))
  }
  const storms = objects.filter((o) => isLocalStorm(o.type) && !o.ghost && o.pose)
  if (at && storms.length) {
    let best: { obj: ResolvedObject; dist: number } | null = null
    for (const t of storms) {
      const pose = t.pose!
      const influence = tornadoImpactRadiusM(t)
      const dist = ringDistanceM(pose, at)
      if (dist > influence) continue
      if (!best || dist < best.dist) best = { obj: t, dist }
    }
    if (best) {
      const surface = findWind(objects)
      const shape = surface ? windSampleFromParams(surface.params, 'surface') : null
      return {
        fromDeg: Number(best.obj.params.fromDeg) || 0,
        plumeDistanceM: shape?.plumeDistanceM || 420,
        plumeSpreadDeg: shape?.plumeSpreadDeg || 26,
        source: stormWindSource(best.obj.type),
      }
    }
  }
  const wind = findWind(objects)
  if (!wind) return null
  return windSampleFromParams(wind.params, 'surface')
}

export function zoneFeatures(
  objects: readonly ResolvedObject[],
  highlightIds: readonly string[] = [],
  selectedId?: string | null,
): FeatureCollection {
  const lit = new Set(highlightIds)
  const features: Feature[] = []
  for (const obj of objects) {
    if (!obj.pose) continue
    if (obj.type === 'wind') continue
    if (isLocalStorm(obj.type)) {
      const hurricane = obj.type === 'hurricane'
      const radiusM = tornadoImpactRadiusM(obj)
      features.push({
        type: 'Feature',
        properties: {
          id: obj.id,
          ghost: obj.ghost ? 1 : 0,
          kind: hurricane ? 'hurricane' : 'tornado',
          lit: lit.has(obj.id) ? 1 : 0,
          selected: obj.id === selectedId ? 1 : 0,
        },
        geometry: { type: 'Polygon', coordinates: [ringAround(obj.pose.lng, obj.pose.lat, radiusM)] },
      })
      features.push({
        type: 'Feature',
        properties: {
          id: obj.id,
          ghost: obj.ghost ? 1 : 0,
          kind: hurricane ? 'hurricane-dir' : 'tornado-dir',
          lit: lit.has(obj.id) ? 1 : 0,
          selected: obj.id === selectedId ? 1 : 0,
        },
        geometry: {
          type: 'Polygon',
          coordinates: [
            directionArrow(
              obj.pose.lng,
              obj.pose.lat,
              Number(obj.params.fromDeg) || 0,
              radiusM,
            ),
          ],
        },
      })
      continue
    }
    const radiusM = Number(obj.params.radiusM)
    if (!Number.isFinite(radiusM) || radiusM <= 0) continue
    features.push({
      type: 'Feature',
      properties: {
        id: obj.id,
        ghost: obj.ghost ? 1 : 0,
        kind:
          obj.type === 'fire'
            ? 'fire'
            : isBrwnPlumeType(obj.type)
              ? BRWN_PLUME_ZONE_KIND
              : 'zone',
        lit: lit.has(obj.id) ? 1 : 0,
        selected: obj.id === selectedId ? 1 : 0,
        tip: zoneCfgFor(obj.type)?.label ?? paletteLabel(obj.type),
      },
      geometry: { type: 'Polygon', coordinates: [ringAround(obj.pose.lng, obj.pose.lat, radiusM)] },
    })
  }
  return { type: 'FeatureCollection', features }
}

/**
 * Distance in the same equirectangular space `ringAround` draws. Haversine
 * disagrees with that ring by enough, near the stroke, to flip a leak in and out.
 */
function ringDistanceM(
  center: { lng: number; lat: number },
  point: { lng: number; lat: number },
): number {
  const cos = Math.cos((center.lat * Math.PI) / 180)
  const dLat = (point.lat - center.lat) * 111_320
  const dLng = (point.lng - center.lng) * 111_320 * cos
  return Math.hypot(dLat, dLng)
}

/**
 * The one wind drawn for a plume source.
 * A tornado or hurricane does not draw a plume. Its direction is the arrow on the ring.
 * Inside that ring, another object uses the storm's direction and the board
 * plume's length. Outside it, Surface wind only. The nearest center wins.
 */
function plumeWindsAt(objects: readonly ResolvedObject[], obj: ResolvedObject): WindSample[] {
  const surfaceObj = findWind(objects)
  const surface = surfaceObj ? windSampleFromParams(surfaceObj.params, 'surface') : null
  if (obj.pose) {
    const storms = objects.filter((o) => isLocalStorm(o.type) && !o.ghost && o.pose)
    let best: { fromDeg: number; dist: number; source: PlumeWindSource } | null = null
    for (const t of storms) {
      const pose = t.pose!
      const influence = tornadoImpactRadiusM(t)
      const dist = ringDistanceM(pose, obj.pose)
      if (dist > influence) continue
      if (!best || dist < best.dist) {
        best = { fromDeg: Number(t.params.fromDeg) || 0, dist, source: stormWindSource(t.type) }
      }
    }
    if (best) {
      return [
        {
          fromDeg: best.fromDeg,
          plumeDistanceM: surface?.plumeDistanceM || 420,
          plumeSpreadDeg: surface?.plumeSpreadDeg || 26,
          source: best.source,
        },
      ]
    }
  }
  return surface ? [surface] : []
}

export function plumeFeatures(
  objects: readonly ResolvedObject[],
  opts?: { /** Personal display pref. Off hides every plume, including tornado. */ show?: boolean },
): FeatureCollection {
  const features: Feature[] = []
  for (const obj of objects) {
    if (obj.ghost || !obj.pose || !isPlumeSource(obj.type)) continue
    if (obj.params.showPlume === false) continue
    if (opts?.show === false) continue
    for (const wind of plumeWindsAt(objects, obj)) {
      features.push({
        type: 'Feature',
        properties: { id: obj.id, windSource: wind.source },
        geometry: {
          type: 'Polygon',
          coordinates: [
            plumeWedge(
              obj.pose.lng,
              obj.pose.lat,
              wind.fromDeg,
              wind.plumeDistanceM,
              wind.plumeSpreadDeg,
            ),
          ],
        },
      })
    }
  }
  return { type: 'FeatureCollection', features }
}

function attachedSegmentsFromParams(params: Record<string, unknown>): [number, number][][] {
  const many = params.attachedAssets
  const list: unknown[] = Array.isArray(many)
    ? many
    : params.attachedAsset && typeof params.attachedAsset === 'object'
      ? [params.attachedAsset]
      : []
  const segs: [number, number][][] = []
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue
    const a = raw as { path?: [number, number][]; segments?: [number, number][][] }
    if (Array.isArray(a.segments) && a.segments.length) {
      for (const seg of a.segments) {
        if (Array.isArray(seg) && seg.length >= 2) segs.push(seg)
      }
    } else if (Array.isArray(a.path) && a.path.length >= 2) {
      segs.push(a.path)
    }
  }
  return segs
}

/** Board line geometry for an object (explicit path/segments, else attached assets). */
export function objectLineSegments(obj: {
  path?: [number, number][] | null
  params: Record<string, unknown>
}): [number, number][][] {
  const extra = obj.params.segments
  if (Array.isArray(extra) && extra.length) {
    return extra.filter((seg): seg is [number, number][] => Array.isArray(seg) && seg.length >= 2)
  }
  if (obj.path && obj.path.length >= 2) return [obj.path]
  return attachedSegmentsFromParams(obj.params)
}

/** Prefer the line geometry for map anchors — drop pose can drift far from the painted path. */
export function objectAnchorLngLat(obj: {
  type?: string
  pose?: { lng: number; lat: number } | null
  path?: [number, number][] | null
  params: Record<string, unknown>
}): { lng: number; lat: number } | null {
  // Point-like board icons keep pose as the drag anchor even when map assets are linked.
  // Using linked street midpoints as the anchor was shifting every linked segment with the icon.
  if (
    obj.pose &&
    obj.type &&
    obj.type !== 'road' &&
    obj.type !== 'sketch'
  ) {
    return { lng: obj.pose.lng, lat: obj.pose.lat }
  }
  const segs = objectLineSegments(obj)
  if (segs.length) {
    const longest = [...segs].sort((a, b) => b.length - a.length)[0]!
    const mid = longest[Math.floor(longest.length / 2)]!
    return { lng: mid[0], lat: mid[1] }
  }
  if (obj.pose) return { lng: obj.pose.lng, lat: obj.pose.lat }
  return null
}

function lineSegments(obj: ResolvedObject): [number, number][][] {
  return objectLineSegments(obj)
}

export function lineFeatures(
  objects: readonly ResolvedObject[],
  selectedId?: string | null,
  highlightIds: readonly string[] = [],
): FeatureCollection {
  const lit = new Set(highlightIds)
  const features: Feature[] = []
  for (const obj of objects) {
    const segs = lineSegments(obj)
    if (!segs.length) continue
    const status = String(obj.effective.status ?? obj.params.status ?? '')
    const closed = obj.type === 'road' && status === 'closed'
    const slow = obj.type === 'road' && status === 'slow'
    const selected = selectedId === obj.id
    const emphasized = selected || lit.has(obj.id)
    const hasAttachedGeom = attachedSegmentsFromParams(obj.params).length > 0
    // Open roads with attachments: skip always-on mesh — asset HL layer owns the stroke.
    if (obj.type === 'road' && !closed && !slow && hasAttachedGeom) continue
    // Non-road objects: linked asset geometry is HL-only (cyan). Never paint amber board strokes
    // for attachments (legacy path/segments on the icon would look "stuck" to the pin).
    if (obj.type !== 'road' && obj.type !== 'sketch' && hasAttachedGeom) continue
    const color =
      obj.type === 'sketch'
        ? String(obj.params.color ?? '#E24B4A')
        : closed
          ? '#E24B4A'
          : slow
            ? '#FF8C1A'
            : obj.type === 'road'
              ? '#4A9EFF'
              : '#FAC775'
    const baseSketchWidth = Number(obj.params.width) || 4
    const baseSketchOpacity = Number(obj.params.opacity) || 0.9
    // Teaching routes can opt out of timeline-lit dimming (keepOpacity).
    const keepSketchOpacity = obj.type === 'sketch' && obj.params.keepOpacity === true
    // Hose / freehand furniture stays readable but quieter until selected or timeline-lit.
    const opacity =
      obj.type === 'sketch'
        ? emphasized || keepSketchOpacity
          ? baseSketchOpacity
          : Math.min(0.38, baseSketchOpacity * 0.42)
        : obj.ghost
          ? 0.35
          : closed
            ? selected
              ? 0.95
              : 0.78
            : slow
              ? selected
                ? 0.98
                : 0.88
              : selected
                ? 1
                : 0.9
    const width =
      obj.type === 'sketch'
        ? emphasized || keepSketchOpacity
          ? baseSketchWidth
          : Math.max(2, baseSketchWidth * 0.55)
        : selected
          ? 11
          : slow || closed
            ? 8
            : 5
    const geometry =
      segs.length === 1
        ? { type: 'LineString' as const, coordinates: segs[0]! }
        : { type: 'MultiLineString' as const, coordinates: segs }
    features.push({
      type: 'Feature',
      properties: {
        id: obj.id,
        kind: obj.type,
        closed: closed ? 1 : 0,
        slow: slow ? 1 : 0,
        // Keep status colors on select — cyan is reserved for linked-asset HL, not selection.
        color,
        width,
        opacity,
        dash: obj.type === 'sketch' && obj.params.dashed === true ? 1 : 0,
        ghost: obj.ghost ? 1 : 0,
        title: obj.label?.trim() || obj.mapLabel?.trim() || obj.type,
      },
      geometry,
    })
  }
  return { type: 'FeatureCollection', features }
}

/**
 * Editor overlay for pose keyframes (user-facing: waypoints).
 * One map pin per contiguous same-spot run (dwell rows share a pin/number).
 * Table/Preview still use full time-sequence indices; `index` is the run's
 * first sequence index (1-based) for drag/focus, `label` is the location number.
 */
export function keyframeOverlayFeatures(
  objects: readonly ResolvedObject[],
  selectedId: string | null,
  focusIndex: number | null = null,
): FeatureCollection {
  if (!selectedId) return { type: 'FeatureCollection', features: [] }
  const obj = objects.find((o) => o.id === selectedId)
  const frames = obj?.keyframes
  if (!obj || !frames?.length) return { type: 'FeatureCollection', features: [] }

  const ordered = normalizePoseKeyframes(frames)
  const runs = poseLocationRuns(ordered)
  const features: Feature[] = []
  if (runs.length >= 2) {
    features.push({
      type: 'Feature',
      properties: { id: obj.id, kind: 'path', selected: 1 },
      geometry: {
        type: 'LineString',
        coordinates: runs.map((r) => [r.lng, r.lat] as [number, number]),
      },
    })
  }
  const points: Feature[] = runs.map((run, runIdx) => {
    const selected =
      focusIndex != null && focusIndex >= run.startIndex && focusIndex <= run.endIndex ? 1 : 0
    const dwell = run.endIndex > run.startIndex ? 1 : 0
    return {
      type: 'Feature' as const,
      properties: {
        id: obj.id,
        kind: 'point',
        /** 1-based first sequence index — map hit testing / drag. */
        index: run.startIndex + 1,
        /** Distinct-location number shown on the pin. */
        label: run.label,
        selected,
        dwell,
        /** Amber dwell pin = hold/wait at this place (same #, later time in the table). */
        title: dwell ? `Pin ${run.label} — hold/wait` : `Pin ${run.label}`,
        // Higher draws later / on top (circle-sort-key + symbol-sort-key).
        z: selected ? 1000 + runIdx : runIdx,
      },
      geometry: { type: 'Point' as const, coordinates: [run.lng, run.lat] },
    }
  })
  // Emit focused pin last so GeoJSON paint order also lifts it above co-located pins.
  const hotIdx = points.findIndex((p) => p.properties?.selected === 1)
  if (hotIdx >= 0) {
    const hot = points[hotIdx]!
    features.push(...points.filter((_, i) => i !== hotIdx), hot)
  } else {
    features.push(...points)
  }
  return { type: 'FeatureCollection', features }
}

/** Sorted pose keyframes for the selected object (same order as table sequence). */
export function orderedPoseKeyframes(
  objects: readonly ResolvedObject[],
  selectedId: string | null,
): { at: string; lng: number; lat: number }[] {
  if (!selectedId) return []
  const frames = objects.find((o) => o.id === selectedId)?.keyframes
  if (!frames?.length) return []
  return normalizePoseKeyframes(frames)
}

/** Build a highlight FeatureCollection from attached asset geometries. */
export function attachedHighlightFeatures(
  objects: readonly { params: Record<string, unknown> }[],
): FeatureCollection {
  const features: Feature[] = []
  for (const obj of objects) {
    const segs = attachedSegmentsFromParams(obj.params)
    if (!segs.length) continue
    features.push({
      type: 'Feature',
      properties: {},
      geometry:
        segs.length === 1
          ? { type: 'LineString', coordinates: segs[0]! }
          : { type: 'MultiLineString', coordinates: segs },
    })
  }
  return { type: 'FeatureCollection', features }
}
